import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  SelectChangeEvent,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { partnerService, UpdatePartnerDto, Partner } from "../../apis/partners";
import { offerTypeService, OfferType } from "../../apis/offerTypes";

interface EditPartnerModalProps {
  open: boolean;
  onClose: () => void;
  partner: Partner | null;
}

const EditPartnerModal: React.FC<EditPartnerModalProps> = ({ open, onClose, partner }) => {
  const [formData, setFormData] = useState<UpdatePartnerDto>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    offerTypeId: "",
    paymentFrequency: 2,
    registrationNumber: "",
    companyName: "",
    password: "",
  });
  const [offerTypes, setOfferTypes] = useState<OfferType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOfferTypes, setLoadingOfferTypes] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOfferTypes();
    }
  }, [open]);

  useEffect(() => {
    if (partner) {
      setFormData({
        firstName: partner.firstName,
        lastName: partner.lastName,
        email: partner.email,
        phone: partner.phone,
        offerTypeId: partner.offerTypeId,
        paymentFrequency: partner.paymentFrequency || 2,
        registrationNumber: partner.registrationNumber || "",
        companyName: partner.companyName || "",
        password: "",
      });
    }
  }, [partner]);

  const fetchOfferTypes = async () => {
    setLoadingOfferTypes(true);
    try {
      const response = await offerTypeService.getActiveOfferTypes();
      if (response.status === "success" || response.status === true) {
        setOfferTypes(response.data.offer_types || []);
      }
    } catch (error) {
      console.error("Failed to fetch offer types:", error);
    } finally {
      setLoadingOfferTypes(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async () => {
    if (!partner) return;

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    // Only include password in update if it's provided
    const updateData = { ...formData };
    if (!updateData.password || updateData.password.trim() === "") {
      delete updateData.password;
    }

    setLoading(true);
    try {
      await partnerService.updatePartner(partner._id, updateData);
      setSnackbar({
        open: true,
        message: "Partner updated successfully",
        severity: "success",
      });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update partner",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Partner</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              label="Registration Number"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Company Name"
              name="companyName"
              value={formData.companyName || ""}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password || ""}
              onChange={handleChange}
              fullWidth
              helperText="Leave blank to keep current password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth required>
              <InputLabel>Offer Type</InputLabel>
              <Select
                name="offerTypeId"
                value={formData.offerTypeId || ""}
                onChange={handleSelectChange}
                label="Offer Type"
                disabled={loadingOfferTypes}
              >
                {offerTypes.map((offerType) => (
                  <MenuItem key={offerType._id} value={offerType._id}>
                    {offerType.name} ({offerType.partner_percentage}% of Teracar Commission)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Payment Frequency</InputLabel>
              <Select
                name="paymentFrequency"
                value={formData.paymentFrequency?.toString() || "2"}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setFormData((prev) => ({ ...prev, paymentFrequency: value }));
                }}
                label="Payment Frequency"
              >
                <MenuItem value="1">1 Week</MenuItem>
                <MenuItem value="2">2 Weeks</MenuItem>
                <MenuItem value="3">3 Weeks</MenuItem>
                <MenuItem value="4">4 Weeks</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? "Updating..." : "Update Partner"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default EditPartnerModal;

